
import { Options, Vue } from 'vue-class-component';
import { SurveyTemplateClient } from '@/services/DueDiligenceServices';
import * as DDM from '@/dueDiligenceModels';
import * as VM from '@/viewModel';
import InfoModal from '@/components/modals/infoModal.vue';
import store from '@/store';

@Options({})
export default class SurveyTemplateCustomizer extends Vue {

    template: DDM.SurveyTemplate = new DDM.SurveyTemplate();
    loaded: boolean = false;
    saving: boolean = false;
    isCustom: boolean = false;
    canEdit: boolean = false;
    expandedSections: boolean[] = [];
    expandedQuestions: { [key: string]: boolean } = {};

    created() {
        this.canEdit = store.state.loggedUser.role != VM.Roles.ReadOnly;

        SurveyTemplateClient.getByCompany()
        .then(x => {
            this.template = x;
            this.isCustom = !!x.company;
            this.ensureLocalizedStrings();
            this.expandedSections = this.template.sections.map(() => false);
            this.loaded = true;
        })
        .catch(() => {
            this.loaded = true;
        });
    }

    get totalWeight(): number {
        return this.template.sections.reduce((sum, s) => sum + (s.weight || 0), 0);
    }

    toggleSection(idx: number) {
        this.expandedSections.splice(idx, 1, !this.expandedSections[idx]);
    }

    toggleQuestion(sIdx: number, qIdx: number) {
        var key = sIdx + '_' + qIdx;
        this.expandedQuestions = { ...this.expandedQuestions, [key]: !this.expandedQuestions[key] };
    }

    isQuestionExpanded(sIdx: number, qIdx: number): boolean {
        return !!this.expandedQuestions[sIdx + '_' + qIdx];
    }

    getSectionTitle(section: DDM.SurveySection): string {
        return section.title?.values?.['en-US'] || section.title?.values?.['it-IT'] || section.sectionId;
    }

    getQuestionText(question: DDM.SurveyQuestion): string {
        var text = question.text?.values?.['en-US'] || question.text?.values?.['it-IT'] || '';
        return text.length > 80 ? text.substring(0, 80) + '...' : text;
    }

    getQuestionTypeLabel(type: DDM.QuestionType): string {
        switch (type) {
            case DDM.QuestionType.YesNoPartially: return 'Yes/No/Partially';
            case DDM.QuestionType.YesNo: return 'Yes/No';
            case DDM.QuestionType.Informational: return 'Info';
            case DDM.QuestionType.CertificationChecklist: return 'Cert. Checklist';
            default: return '?';
        }
    }

    getRedFlagLabel(type: DDM.RedFlagType): string {
        switch (type) {
            case DDM.RedFlagType.RedFlag: return 'RF';
            case DDM.RedFlagType.HighRedFlag: return 'HRF';
            case DDM.RedFlagType.ReverseHighRedFlag: return 'Rev-HRF';
            default: return '';
        }
    }

    ensureLocalizedStrings() {
        for (const section of this.template.sections) {
            if (!section.title?.values) section.title = new DDM.LocalizedString();
            if (!section.title.values['en-US']) section.title.values['en-US'] = '';
            if (!section.title.values['it-IT']) section.title.values['it-IT'] = '';
            if (!section.description?.values) section.description = new DDM.LocalizedString();

            for (const q of section.questions) {
                if (!q.text?.values) q.text = new DDM.LocalizedString();
                if (!q.text.values['en-US']) q.text.values['en-US'] = '';
                if (!q.text.values['it-IT']) q.text.values['it-IT'] = '';
                if (q.allowFileUpload === undefined) q.allowFileUpload = false;
                if (q.isCertificateDocument === undefined) q.isCertificateDocument = false;

                if (q.subQuestions) {
                    for (const sub of q.subQuestions) {
                        if (!sub.text?.values) sub.text = new DDM.LocalizedString();
                        if (!sub.text.values['en-US']) sub.text.values['en-US'] = '';
                        if (!sub.text.values['it-IT']) sub.text.values['it-IT'] = '';
                        if (sub.allowFileUpload === undefined) sub.allowFileUpload = false;
                        if (sub.isCertificateDocument === undefined) sub.isCertificateDocument = false;
                        if (sub.scoreGroup === undefined) sub.scoreGroup = "";
                        if (sub.scoreGroupRank === undefined) sub.scoreGroupRank = 0;
                    }
                }
            }
        }
    }

    addSection() {
        var newSection = new DDM.SurveySection();
        newSection.sectionId = 'S' + (this.template.sections.length + 2);
        newSection.sortOrder = this.template.sections.length + 1;
        newSection.title.values['en-US'] = '';
        newSection.title.values['it-IT'] = '';
        this.template.sections.push(newSection);
        this.expandedSections.push(true);
    }

    removeSection(idx: number) {
        if (confirm('Remove this section?')) {
            this.template.sections.splice(idx, 1);
            this.expandedSections.splice(idx, 1);
        }
    }

    addQuestion(section: DDM.SurveySection) {
        var newQ = new DDM.SurveyQuestion();
        var maxSort = section.questions.reduce((max, q) => Math.max(max, q.sortOrder), 0);
        newQ.questionId = section.sectionId + '_Q' + (section.questions.length + 1);
        newQ.sortOrder = maxSort + 1;
        newQ.text.values['en-US'] = '';
        newQ.text.values['it-IT'] = '';
        section.questions.push(newQ);
    }

    removeQuestion(section: DDM.SurveySection, idx: number) {
        if (confirm('Remove this question?')) {
            section.questions.splice(idx, 1);
        }
    }

    addSubQuestion(question: DDM.SurveyQuestion) {
        if (!question.subQuestions) question.subQuestions = [];
        var sub = new DDM.SurveySubQuestion();
        sub.subQuestionId = question.questionId + '_' + (question.subQuestions.length + 1);
        sub.text.values['en-US'] = '';
        sub.text.values['it-IT'] = '';
        question.subQuestions.push(sub);
    }

    removeSubQuestion(question: DDM.SurveyQuestion, idx: number) {
        question.subQuestions.splice(idx, 1);
    }

    resetToDefault() {
        this.$opModal.show(InfoModal, {
            text: this.$localizationService.getLocalizedValue('Reset to Default', 'Reset to Default'),
            subtitle: this.$localizationService.getLocalizedValue(
                'This will discard all customizations and restore the default template. Continue?',
                'This will discard all customizations and restore the default template. Continue?'
            ),
            confirm: this.$localizationService.getLocalizedValue('Reset', 'Reset'),
            confirmCb: () => {
                this.$opModal.closeLast();
                SurveyTemplateClient.resetToDefault()
                .then(x => {
                    this.template = x;
                    this.isCustom = false;
                    this.ensureLocalizedStrings();
                    this.expandedSections = this.template.sections.map(() => false);
                    this.$opModal.show(InfoModal, {
                        text: this.$localizationService.getLocalizedValue('Success', 'Success'),
                        subtitle: this.$localizationService.getLocalizedValue('Template reset to default', 'Template reset to default'),
                        deny: this.$localizationService.getLocalizedValue('Close', 'Close'),
                        denyCb: () => { this.$opModal.closeLast(); }
                    });
                });
            },
            deny: this.$localizationService.getLocalizedValue('Cancel', 'Cancel'),
            denyCb: () => { this.$opModal.closeLast(); }
        });
    }

    save() {
        if (this.totalWeight != 100) {
            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue('Validation Error', 'Validation Error'),
                subtitle: 'Section weights must sum to 100. Current total: ' + this.totalWeight,
                deny: this.$localizationService.getLocalizedValue('Close', 'Close'),
                denyCb: () => { this.$opModal.closeLast(); }
            });
            return;
        }

        this.saving = true;
        SurveyTemplateClient.saveCompanyTemplate(this.template)
        .then(x => {
            this.template = x;
            this.isCustom = true;
            this.ensureLocalizedStrings();
            this.saving = false;
            this.$opModal.show(InfoModal, {
                text: this.$localizationService.getLocalizedValue('Success', 'Success'),
                subtitle: 'Template saved (v' + this.template.version + ')',
                deny: this.$localizationService.getLocalizedValue('Close', 'Close'),
                denyCb: () => { this.$opModal.closeLast(); }
            });
        })
        .catch(() => {
            this.saving = false;
        });
    }
}
